import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Button, Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, FormFeedback,
    Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';

import Widget from '../../../components/Widget/Widget';
import LayoutCanvasEditor from './LayoutCanvasEditor';
import StationLayoutBindingControls from '../StationLayoutBindingControls';

import {
    loadVenues, createVenue, updateVenue, deactivateVenue,
    loadLayouts, loadLayoutDetail, createLayout, updateLayout, deactivateLayout,
    fetchStationLayoutSelection, saveStationLayoutSelection,
    listEventSeatings, previewLayoutImport, commitLayoutImport
} from '../../../actions/venues';
import { loadAvailableStations } from '../../../actions/settings';

import s from './LayoutSettings.module.scss';
import { withTranslation } from '../../../i18n/LanguageContext';

const EMPTY_LAYOUT_JSON = JSON.stringify({ objects: [] }, null, 2);

function countLayoutObjects(raw) {
    try {
        const parsed = JSON.parse(raw || '{}');
        if (Array.isArray(parsed)) return parsed.length;
        if (Array.isArray(parsed && parsed.objects)) return parsed.objects.length;
        if (Array.isArray(parsed && parsed.Objects)) return parsed.Objects.length;
    } catch (_) {
        return 0;
    }
    return 0;
}

class LayoutSettings extends React.Component {

    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        venues: PropTypes.array,
        layoutsByVenue: PropTypes.object,
        currentLayout: PropTypes.object,
        isLoading: PropTypes.bool,
        isMutating: PropTypes.bool,
        merchant: PropTypes.object,
        stations: PropTypes.array
    };

    static defaultProps = {
        venues: [],
        layoutsByVenue: {},
        currentLayout: null,
        isLoading: false,
        isMutating: false,
        stations: []
    };

    state = {
        // venue modal
        venueModalOpen: false,
        venueDraft: { VenueId: 0, Name: '', Description: '', IsActive: true, EventVenueId: '' },
        venueErrors: {},

        // layout modal
        layoutModalOpen: false,
        layoutTab: 'canvas',
        layoutDraft: {
            LayoutId: 0, VenueId: 0, Name: '', Description: '',
            Width: 1200, Height: 800, IsActive: true, LayoutData: EMPTY_LAYOUT_JSON
        },
        originalLayoutData: EMPTY_LAYOUT_JSON,
        layoutErrors: {},
        jsonError: '',

        // station bindings: keyed by StationId
        // { [stationId]: { EnableLayoutMode, PosVenueId, PosLayoutId, saving } }
        stationSelections: {},

        // Fabric → POS import wizard (Create-new only in Phase 1)
        importModalOpen: false,
        importVenue: null,
        importSeatings: [],
        importSeatingId: '',
        importPreview: null,
        importLayoutName: '',
        importLoading: false,
        importCommitting: false,
        importError: ''
    };

    componentDidMount() {
        this.props.dispatch(loadVenues(true));
        if (this.props.merchant && this.props.merchant.MerchantId) {
            this.props.dispatch(loadAvailableStations(this.props.merchant.MerchantId));
        }
        this.ensureLayoutsLoaded(this.props.venues);
        if (this.props.stations && this.props.stations.length) {
            this.hydrateStationSelections(this.props.stations);
        }
    }

    componentDidUpdate(prevProps) {
        const merchantId = this.props.merchant && this.props.merchant.MerchantId;
        const prevMerchantId = prevProps.merchant && prevProps.merchant.MerchantId;
        if (merchantId && merchantId !== prevMerchantId) {
            this.props.dispatch(loadAvailableStations(merchantId));
        }
        // When stations land, hydrate their layout selections.
        if (prevProps.stations !== this.props.stations) {
            this.hydrateStationSelections(this.props.stations);
        }
        // Eager-load layouts for every venue so station pickers always have options.
        if (prevProps.venues !== this.props.venues) {
            this.ensureLayoutsLoaded(this.props.venues);
        }
    }

    ensureLayoutsLoaded = (venues) => {
        (venues || []).forEach((v) => {
            if (!v || !v.VenueId) return;
            if (this.props.layoutsByVenue[v.VenueId]) return;
            this.props.dispatch(loadLayouts(v.VenueId, true));
        });
    };

    hydrateStationSelections = async (stations) => {
        if (!Array.isArray(stations) || stations.length === 0) return;
        const next = { ...this.state.stationSelections };
        let changed = false;
        const venueIdsToLoad = new Set();
        for (const st of stations) {
            // Skip only server-hydrated rows; a field touch without _hydrated must refresh.
            if (next[st.StationId] && next[st.StationId]._hydrated) continue;
            const sel = await fetchStationLayoutSelection(st.StationId);
            next[st.StationId] = sel
                ? {
                    EnableLayoutMode: !!sel.EnableLayoutMode,
                    PosVenueId: sel.PosVenueId || null,
                    PosLayoutId: sel.PosLayoutId || null,
                    saving: false,
                    _hydrated: true,
                  }
                : {
                    EnableLayoutMode: false,
                    PosVenueId: null,
                    PosLayoutId: null,
                    saving: false,
                    _hydrated: true,
                  };
            if (next[st.StationId].PosVenueId) {
                venueIdsToLoad.add(next[st.StationId].PosVenueId);
            }
            changed = true;
        }
        if (changed) this.setState({ stationSelections: next });
        venueIdsToLoad.forEach((venueId) => {
            if (!this.props.layoutsByVenue[venueId]) {
                this.props.dispatch(loadLayouts(venueId, true));
            }
        });
    };

    // ---------- Venue ----------
    openCreateVenue = () => {
        this.setState({
            venueModalOpen: true,
            venueErrors: {},
            venueDraft: { VenueId: 0, Name: '', Description: '', IsActive: true, EventVenueId: '' }
        });
    };

    openEditVenue = (v) => {
        this.setState({
            venueModalOpen: true,
            venueErrors: {},
            venueDraft: {
                VenueId: v.VenueId,
                Name: v.Name || '',
                Description: v.Description || '',
                IsActive: !!v.IsActive,
                EventVenueId: v.EventVenueId == null ? '' : String(v.EventVenueId)
            }
        });
    };

    closeVenueModal = () => this.setState({ venueModalOpen: false });

    onVenueField = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        this.setState({ venueDraft: { ...this.state.venueDraft, [field]: value } });
    };

    saveVenue = async () => {
        const draft = this.state.venueDraft;
        const errors = {};
        if (!draft.Name || !draft.Name.trim()) errors.Name = 'Name is required';
        if (draft.EventVenueId !== '' && Number.isNaN(parseInt(draft.EventVenueId, 10))) {
            errors.EventVenueId = 'Must be a number';
        }
        if (Object.keys(errors).length) { this.setState({ venueErrors: errors }); return; }

        const payload = {
            VenueId: draft.VenueId,
            Name: draft.Name.trim(),
            Description: draft.Description || null,
            IsActive: draft.IsActive,
            EventVenueId: draft.EventVenueId === '' ? null : parseInt(draft.EventVenueId, 10)
        };

        const ok = draft.VenueId > 0
            ? await this.props.dispatch(updateVenue(payload))
            : (await this.props.dispatch(createVenue(payload))) != null;

        if (ok) this.closeVenueModal();
    };

    deactivateVenue = (v) => {
        if (window.confirm(`Deactivate venue "${v.Name}"? Layouts assigned to it will remain but the venue will be hidden from selection.`)) {
            this.props.dispatch(deactivateVenue(v.VenueId));
        }
    };

    // ---------- Fabric → POS import ----------
    openImportModal = async (venue) => {
        if (!venue || !venue.EventVenueId) return;
        this.setState({
            importModalOpen: true,
            importVenue: venue,
            importSeatings: [],
            importSeatingId: '',
            importPreview: null,
            importLayoutName: '',
            importLoading: true,
            importCommitting: false,
            importError: ''
        });
        const seatings = await listEventSeatings(venue.VenueId);
        const preferred = (seatings || []).find(s => s.IsDefault) || (seatings || [])[0];
        this.setState({
            importSeatings: seatings || [],
            importSeatingId: preferred ? String(preferred.VenueSeatingId) : '',
            importLayoutName: preferred && preferred.Name
                ? `${preferred.Name} (imported)`
                : 'Imported layout',
            importLoading: false,
            importError: (seatings || []).length ? '' : this.props.t('layouts.importNoSeatings')
        });
    };

    closeImportModal = () => {
        if (this.state.importCommitting) return;
        this.setState({
            importModalOpen: false,
            importVenue: null,
            importSeatings: [],
            importSeatingId: '',
            importPreview: null,
            importLayoutName: '',
            importLoading: false,
            importCommitting: false,
            importError: ''
        });
    };

    onImportSeatingChange = (e) => {
        const seatingId = e.target.value;
        const seating = (this.state.importSeatings || []).find(s => String(s.VenueSeatingId) === String(seatingId));
        this.setState({
            importSeatingId: seatingId,
            importPreview: null,
            importError: '',
            importLayoutName: seating && seating.Name
                ? `${seating.Name} (imported)`
                : this.state.importLayoutName
        });
    };

    runImportPreview = async () => {
        const venue = this.state.importVenue;
        if (!venue) return;
        this.setState({ importLoading: true, importError: '', importPreview: null });
        const seatingId = this.state.importSeatingId ? parseInt(this.state.importSeatingId, 10) : null;
        const preview = await previewLayoutImport(venue.VenueId, seatingId);
        if (!preview) {
            this.setState({ importLoading: false, importError: this.props.t('layouts.importPreviewFailed') });
            return;
        }
        const mapped = (preview.stats && preview.stats.mapped) || 0;
        this.setState({
            importLoading: false,
            importPreview: preview,
            importError: mapped <= 0 ? this.props.t('layouts.importEmptyMap') : ''
        });
    };

    commitImport = async () => {
        const venue = this.state.importVenue;
        const preview = this.state.importPreview;
        if (!venue || !preview) return;
        const mapped = (preview.stats && preview.stats.mapped) || 0;
        if (mapped <= 0) {
            this.setState({ importError: this.props.t('layouts.importEmptyMap') });
            return;
        }
        this.setState({ importCommitting: true, importError: '' });
        const seatingId = this.state.importSeatingId ? parseInt(this.state.importSeatingId, 10) : null;
        const result = await this.props.dispatch(commitLayoutImport(venue.VenueId, {
            VenueSeatingId: seatingId,
            LayoutName: (this.state.importLayoutName || '').trim() || undefined,
            ReplaceObjects: false
        }));
        this.setState({ importCommitting: false });
        if (!result || !result.layoutId) {
            this.setState({ importError: this.props.t('layouts.importCommitFailed') });
            return;
        }
        this.closeImportModal();
        // Open the new layout for touch-up without auto-binding stations.
        await this.openEditLayout({ LayoutId: result.layoutId });
    };

    // ---------- Layouts ----------
    expandVenueLayouts = (venueId) => {
        if (!this.props.layoutsByVenue[venueId]) {
            this.props.dispatch(loadLayouts(venueId, true));
        }
    };

    openCreateLayout = (venue) => {
        this.setState({
            layoutModalOpen: true,
            layoutErrors: {},
            jsonError: '',
            originalLayoutData: EMPTY_LAYOUT_JSON,
            layoutDraft: {
                LayoutId: 0, VenueId: venue.VenueId, Name: '', Description: '',
                Width: 1200, Height: 800, IsActive: true, LayoutData: EMPTY_LAYOUT_JSON
            }
        });
    };

    openEditLayout = async (layout) => {
        // Hydrate full detail (incl. LayoutData)
        const detail = await this.props.dispatch(loadLayoutDetail(layout.LayoutId));
        if (!detail) return;
        let formatted = detail.LayoutData || EMPTY_LAYOUT_JSON;
        try { formatted = JSON.stringify(JSON.parse(formatted), null, 2); } catch (_) { /* leave as-is */ }
        this.setState({
            layoutModalOpen: true,
            layoutErrors: {},
            jsonError: '',
            originalLayoutData: formatted,
            layoutDraft: {
                LayoutId: detail.LayoutId,
                VenueId: detail.VenueId,
                Name: detail.Name || '',
                Description: detail.Description || '',
                Width: detail.Width || 1200,
                Height: detail.Height || 800,
                IsActive: !!detail.IsActive,
                LayoutData: formatted
            }
        });
    };

    closeLayoutModal = () => this.setState({
        layoutModalOpen: false,
        layoutTab: 'canvas',
        originalLayoutData: EMPTY_LAYOUT_JSON,
    });

    onLayoutField = (field) => (e) => {
        let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        if (field === 'Width' || field === 'Height') value = value === '' ? '' : parseInt(value, 10);
        this.setState({ layoutDraft: { ...this.state.layoutDraft, [field]: value } });
    };

    onLayoutJson = (e) => {
        const value = e.target.value;
        let jsonError = '';
        try { JSON.parse(value); } catch (err) { jsonError = err.message; }
        this.setState({ layoutDraft: { ...this.state.layoutDraft, LayoutData: value }, jsonError });
    };

    saveLayout = async () => {
        const draft = this.state.layoutDraft;
        const { originalLayoutData } = this.state;
        const errors = {};
        if (!draft.Name || !draft.Name.trim()) errors.Name = 'Name is required';
        if (!draft.Width || draft.Width <= 0) errors.Width = 'Width must be > 0';
        if (!draft.Height || draft.Height <= 0) errors.Height = 'Height must be > 0';

        // Validate JSON
        let jsonError = '';
        try { JSON.parse(draft.LayoutData || '{}'); }
        catch (err) { jsonError = err.message; }

        if (Object.keys(errors).length || jsonError) {
            this.setState({ layoutErrors: errors, jsonError });
            return;
        }

        const originalObjectCount = countLayoutObjects(originalLayoutData);
        const nextObjectCount = countLayoutObjects(draft.LayoutData);
        if (draft.LayoutId > 0 && originalObjectCount > 0 && nextObjectCount === 0) {
            const confirmed = window.confirm(
                'This layout previously contained objects, but the current payload is empty. Saving now will remove all objects from the layout. Continue?'
            );
            if (!confirmed) return;
        }

        const payload = {
            LayoutId: draft.LayoutId,
            VenueId: draft.VenueId,
            Name: draft.Name.trim(),
            Description: draft.Description || null,
            Width: draft.Width,
            Height: draft.Height,
            IsActive: draft.IsActive,
            LayoutData: draft.LayoutData
        };

        const ok = draft.LayoutId > 0
            ? await this.props.dispatch(updateLayout(payload))
            : (await this.props.dispatch(createLayout(payload))) != null;

        if (ok) this.closeLayoutModal();
    };

    deactivateLayoutClicked = (layout) => {
        if (window.confirm(`Deactivate layout "${layout.Name}"?`)) {
            this.props.dispatch(deactivateLayout(layout.LayoutId, layout.VenueId));
        }
    };

    // ---------- station bindings ----------
    setStationField = (stationId, field, value) => {
        const cur = this.state.stationSelections[stationId] ||
            { EnableLayoutMode: false, PosVenueId: null, PosLayoutId: null, saving: false, _hydrated: false };
        const next = { ...cur, [field]: value };
        // Clear PosLayoutId if venue changes
        if (field === 'PosVenueId') next.PosLayoutId = null;
        this.setState({
            stationSelections: { ...this.state.stationSelections, [stationId]: next }
        }, () => {
            // If venue just changed, ensure its layouts are loaded (incl. inactive for editor parity)
            if (field === 'PosVenueId' && value) {
                if (!this.props.layoutsByVenue[value]) {
                    this.props.dispatch(loadLayouts(value, true));
                }
            }
        });
    };

    saveStationBinding = async (stationId) => {
        const sel = this.state.stationSelections[stationId];
        if (!sel || !sel._hydrated) {
            window.alert(this.props.t('layouts.layoutLoading'));
            return;
        }
        // Cannot enable layout mode without picking a venue + layout
        if (sel.EnableLayoutMode && (!sel.PosVenueId || !sel.PosLayoutId)) {
            window.alert(this.props.t('layouts.pickVenueLayout'));
            return;
        }
        this.setState({
            stationSelections: {
                ...this.state.stationSelections,
                [stationId]: { ...sel, saving: true }
            }
        });
        const ok = await saveStationLayoutSelection({
            StationId: stationId,
            EnableLayoutMode: !!sel.EnableLayoutMode,
            PosVenueId: sel.PosVenueId || null,
            PosLayoutId: sel.PosLayoutId || null
        });
        this.setState({
            stationSelections: {
                ...this.state.stationSelections,
                [stationId]: { ...sel, saving: false, _hydrated: true }
            }
        });
        if (!ok) {
            window.alert(this.props.t('layouts.layoutSaveFailed'));
        }
    };

    // ---------- render helpers ----------
    renderVenueRow = (v) => {
        const layouts = this.props.layoutsByVenue[v.VenueId] || null;
        return (
            <div key={v.VenueId} className={s.venueCard}>
                <div className={s.venueCardHeader}>
                    <div>
                        <h6 className={s.venueName}>
                            {v.Name}
                            {!v.IsActive && <span className={s.inactivePill} style={{ marginLeft: 8 }}>{this.props.t('layouts.inactive')}</span>}
                        </h6>
                        {v.Description && <p className={s.venueDesc}>{v.Description}</p>}
                        <div className={s.venueMeta}>
                            VenueId: {v.VenueId}
                            {v.EventVenueId != null && <> &middot; EventVenueId: {v.EventVenueId}</>}
                        </div>
                    </div>
                    <div className={s.cardActions}>
                        {v.EventVenueId != null && (
                            <button
                                type="button"
                                className={s.outlineBtn}
                                onClick={() => this.openImportModal(v)}
                                title={this.props.t('layouts.importFromEventVenueHelp')}
                            >
                                {this.props.t('layouts.importFromEventVenue')}
                            </button>
                        )}
                        <button type="button" className={s.secondaryBtn} onClick={() => this.openEditVenue(v)}>{this.props.t('common.edit')}</button>
                        {v.IsActive && (
                            <button type="button" className={s.dangerBtn} onClick={() => this.deactivateVenue(v)}>{this.props.t('layouts.deactivate')}</button>
                        )}
                    </div>
                </div>

                <div className={s.layoutsRow}>
                    {layouts == null && (
                        <button type="button" className={s.linkBtn} onClick={() => this.expandVenueLayouts(v.VenueId)}>
                            {this.props.t('layouts.showLayouts')}
                        </button>
                    )}
                    {layouts && layouts.length === 0 && (
                        <div className={s.helpText}>{this.props.t('layouts.noLayouts')}</div>
                    )}
                    {layouts && layouts.map(l => (
                        <div key={l.LayoutId} className={s.layoutRowItem}>
                            <span>
                                {l.Name}{' '}
                                <span className={s.helpText}>({l.Width}×{l.Height})</span>
                                {!l.IsActive && <span className={s.inactivePill} style={{ marginLeft: 6 }}>{this.props.t('layouts.inactive')}</span>}
                            </span>
                            <span className={s.cardActions}>
                                <button type="button" className={s.linkBtn} onClick={() => this.openEditLayout(l)}>{this.props.t('common.edit')}</button>
                                {l.IsActive && (
                                    <button type="button" className={s.linkBtn} onClick={() => this.deactivateLayoutClicked(l)}>
                                        {this.props.t('layouts.deactivate')}
                                    </button>
                                )}
                            </span>
                        </div>
                    ))}
                    <div>
                        <button type="button" className={s.outlineBtn} onClick={() => this.openCreateLayout(v)}>
                            <i className="zmdi zmdi-plus" /> {this.props.t('layouts.newLayout')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { venues, isMutating, stations, layoutsByVenue, t } = this.props;
        const { venueModalOpen, venueDraft, venueErrors,
                layoutModalOpen, layoutTab, layoutDraft, layoutErrors, jsonError,
                stationSelections,
                importModalOpen, importVenue, importSeatings, importSeatingId,
                importPreview, importLayoutName, importLoading, importCommitting, importError } = this.state;
        const activeVenues = venues.filter(v => v.IsActive);

        return (
            <div className={s.page}>
                <Widget title={t('layouts.title')} className={s.layoutWidget}>
                    <div className={s.layoutGrid}>
                        <div className={s.layoutColumn}>
                            <div className={s.sectionCard}>
                                <div className={s.sectionCardHeader}>{t('layouts.venuesHeader')}</div>
                                <div className={s.sectionCardBody}>
                                    <p className={s.noteText}>{t('layouts.venuesIntro')}</p>
                                    <button
                                        type="button"
                                        className={s.primaryBtn}
                                        onClick={this.openCreateVenue}
                                    >
                                        <i className="zmdi zmdi-plus" /> {t('layouts.newVenue')}
                                    </button>
                                    {venues.length === 0 ? (
                                        <p className={s.helpText}>
                                            {t('layouts.noVenues')}
                                        </p>
                                    ) : (
                                        <div className={s.venueList}>
                                            {venues.map(this.renderVenueRow)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={s.layoutColumn}>
                            <div className={s.sectionCard}>
                                <div className={s.sectionCardHeader}>{t('layouts.stationHeader')}</div>
                                <div className={s.sectionCardBody}>
                                    <p className={s.noteText}>{t('layouts.stationIntro')}</p>
                                    {stations.length === 0 ? (
                                        <p className={s.helpText}>{t('layouts.noStations')}</p>
                                    ) : (
                                        <div className={s.stationBindings}>
                                            {stations.map(st => {
                                                const sel = stationSelections[st.StationId] ||
                                                    { EnableLayoutMode: false, PosVenueId: null, PosLayoutId: null, saving: false };
                                                const venueLayouts = sel.PosVenueId ? (layoutsByVenue[sel.PosVenueId] || []) : [];
                                                return (
                                                    <div key={st.StationId} className={s.stationRow}>
                                                        <div className={s.stationName}>
                                                            <strong>{st.Name}</strong>
                                                            <div className={s.helpText}>StationId: {st.StationId}</div>
                                                        </div>
                                                        <div className={s.stationDropdowns}>
                                                            <StationLayoutBindingControls
                                                                stationId={st.StationId}
                                                                selection={sel}
                                                                venues={activeVenues}
                                                                layouts={venueLayouts}
                                                                t={t}
                                                                showSave={false}
                                                                onChange={(field, value) => this.setStationField(st.StationId, field, value)}
                                                            />
                                                        </div>
                                                        <div className={s.stationSave}>
                                                            <button
                                                                type="button"
                                                                className={s.primaryBtnSm}
                                                                disabled={sel.saving}
                                                                onClick={() => this.saveStationBinding(st.StationId)}
                                                            >
                                                                {sel.saving ? t('common.saving') : t('common.save')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <p className={s.infoNote}>
                                <strong>{t('layouts.note')}</strong> {t('layouts.noteBody')}
                            </p>
                        </div>
                    </div>
                </Widget>

                {/* ---------- Venue modal ---------- */}
                <Modal isOpen={venueModalOpen} toggle={this.closeVenueModal} modalClassName={s.darkModal}>
                    <ModalHeader toggle={this.closeVenueModal}>
                        {venueDraft.VenueId > 0 ? t('layouts.editVenue') : t('layouts.newVenue')}
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label>{t('layouts.nameRequired')}</Label>
                                <Input value={venueDraft.Name} onChange={this.onVenueField('Name')}
                                       invalid={!!venueErrors.Name} />
                                <FormFeedback>{venueErrors.Name}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('layouts.description')}</Label>
                                <Input type="textarea" rows="2"
                                       value={venueDraft.Description}
                                       onChange={this.onVenueField('Description')} />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('layouts.eventVenueId')}</Label>
                                <Input value={venueDraft.EventVenueId}
                                       onChange={this.onVenueField('EventVenueId')}
                                       placeholder={t('layouts.eventVenuePlaceholder')}
                                       invalid={!!venueErrors.EventVenueId} />
                                <FormFeedback>{venueErrors.EventVenueId}</FormFeedback>
                            </FormGroup>
                            <FormGroup check>
                                <Label check>
                                    <Input type="checkbox" checked={venueDraft.IsActive}
                                           onChange={this.onVenueField('IsActive')} />{' '}
                                    {t('common.active')}
                                </Label>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={this.closeVenueModal}>{t('common.cancel')}</Button>
                        <Button color="primary" onClick={this.saveVenue} disabled={isMutating}>
                            {isMutating ? t('common.saving') : t('common.save')}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* ---------- Layout modal ---------- */}
                <Modal isOpen={layoutModalOpen} toggle={this.closeLayoutModal} size="xl" modalClassName={s.darkModal}>
                    <ModalHeader toggle={this.closeLayoutModal}>
                        {layoutDraft.LayoutId > 0 ? t('layouts.editLayout') : t('layouts.newLayoutTitle')}
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup style={{ display: 'flex', gap: 12 }}>
                                <div style={{ flex: 2 }}>
                                    <Label>{t('layouts.nameRequired')}</Label>
                                    <Input value={layoutDraft.Name} onChange={this.onLayoutField('Name')}
                                           invalid={!!layoutErrors.Name} />
                                    <FormFeedback>{layoutErrors.Name}</FormFeedback>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Label>Width (px)</Label>
                                    <Input type="number" min={100}
                                           value={layoutDraft.Width}
                                           onChange={this.onLayoutField('Width')}
                                           invalid={!!layoutErrors.Width} />
                                    <FormFeedback>{layoutErrors.Width}</FormFeedback>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Label>Height (px)</Label>
                                    <Input type="number" min={100}
                                           value={layoutDraft.Height}
                                           onChange={this.onLayoutField('Height')}
                                           invalid={!!layoutErrors.Height} />
                                    <FormFeedback>{layoutErrors.Height}</FormFeedback>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('layouts.description')}</Label>
                                <Input type="textarea" rows="2"
                                       value={layoutDraft.Description}
                                       onChange={this.onLayoutField('Description')} />
                            </FormGroup>
                            <FormGroup>
                                <Nav tabs className="mb-2">
                                    <NavItem>
                                        <NavLink
                                            active={layoutTab === 'canvas'}
                                            onClick={() => this.setState({ layoutTab: 'canvas' })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Canvas Editor
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            active={layoutTab === 'json'}
                                            onClick={() => this.setState({ layoutTab: 'json' })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            JSON
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <TabContent activeTab={layoutTab}>
                                    <TabPane tabId="canvas">
                                        {layoutModalOpen && (
                                            <LayoutCanvasEditor
                                                key={layoutDraft.LayoutId || 'new-layout'}
                                                width={layoutDraft.Width || 1200}
                                                height={layoutDraft.Height || 800}
                                                value={layoutDraft.LayoutData}
                                                onChange={(json) => {
                                                    let jsonError = '';
                                                    try { JSON.parse(json); } catch (err) { jsonError = err.message; }
                                                    this.setState({
                                                        layoutDraft: { ...this.state.layoutDraft, LayoutData: json },
                                                        jsonError
                                                    });
                                                }}
                                            />
                                        )}
                                    </TabPane>
                                    <TabPane tabId="json">
                                        <Input type="textarea" className={s.jsonEditor}
                                               value={layoutDraft.LayoutData}
                                               onChange={this.onLayoutJson}
                                               invalid={!!jsonError} />
                                        {jsonError ? (
                                            <div className="invalid-feedback d-block">{jsonError}</div>
                                        ) : (
                                            <div className={s.helpText}>
                                                Schema: <code>{`{ "objects": [{ "id": 1, "type": "table_rect", "label": "T1", "x": 100, "y": 100, "width": 60, "height": 60, "seatCount": 4 }] }`}</code>
                                            </div>
                                        )}
                                    </TabPane>
                                </TabContent>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <FormGroup check className="mr-auto mb-0">
                            <Label check>
                                <Input type="checkbox" checked={layoutDraft.IsActive}
                                       onChange={this.onLayoutField('IsActive')} />{' '}
                                Active
                            </Label>
                        </FormGroup>
                        <Button color="secondary" outline onClick={this.closeLayoutModal}>{t('common.cancel')}</Button>
                        <Button color="primary" onClick={this.saveLayout} disabled={isMutating || !!jsonError}>
                            {isMutating ? t('common.saving') : t('common.save')}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* ---------- Import from Event Venue (Create-new) ---------- */}
                <Modal
                    isOpen={importModalOpen}
                    toggle={this.closeImportModal}
                    size="lg"
                    modalClassName={s.darkModal}
                    backdrop={importCommitting ? 'static' : true}
                >
                    <ModalHeader toggle={importCommitting ? undefined : this.closeImportModal}>
                        {t('layouts.importFromEventVenue')}
                        {importVenue ? ` — ${importVenue.Name}` : ''}
                    </ModalHeader>
                    <ModalBody>
                        <p className={s.noteText}>{t('layouts.importIntro')}</p>
                        <Form>
                            <FormGroup>
                                <Label>{t('layouts.importSeatingLayer')}</Label>
                                <Input
                                    type="select"
                                    value={importSeatingId}
                                    onChange={this.onImportSeatingChange}
                                    disabled={importLoading || importCommitting || !(importSeatings || []).length}
                                >
                                    {(importSeatings || []).length === 0 && (
                                        <option value="">{t('layouts.importNoSeatings')}</option>
                                    )}
                                    {(importSeatings || []).map(s => (
                                        <option key={s.VenueSeatingId} value={s.VenueSeatingId}>
                                            {s.Name || `Seating ${s.VenueSeatingId}`}
                                            {s.IsDefault ? ` (${t('layouts.importDefault')})` : ''}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('layouts.importLayoutName')}</Label>
                                <Input
                                    value={importLayoutName}
                                    onChange={(e) => this.setState({ importLayoutName: e.target.value })}
                                    disabled={importCommitting}
                                />
                            </FormGroup>
                        </Form>

                        {importError && <p className={s.importError}>{importError}</p>}

                        {importPreview && (
                            <div className={s.importPreview}>
                                <div className={s.venueMeta}>
                                    {t('layouts.importMapped')}: {(importPreview.stats && importPreview.stats.mapped) || 0}
                                    {' · '}
                                    {t('layouts.importSkipped')}: {(importPreview.stats && importPreview.stats.skipped) || 0}
                                    {' · '}
                                    {t('layouts.importConverter')}: {importPreview.converterVersion}
                                </div>
                                {(importPreview.skipped || []).length > 0 && (
                                    <ul className={s.importSkipList}>
                                        {[...new Set((importPreview.skipped || []).map(x => x.otype))].map(otype => (
                                            <li key={otype}>{t('layouts.importSkipReason', { otype })}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            outline
                            onClick={this.closeImportModal}
                            disabled={importCommitting}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={this.runImportPreview}
                            disabled={importLoading || importCommitting || !importSeatingId}
                        >
                            {importLoading ? t('layouts.importPreviewing') : t('layouts.importPreview')}
                        </Button>
                        <Button
                            color="primary"
                            onClick={this.commitImport}
                            disabled={
                                importLoading
                                || importCommitting
                                || !importPreview
                                || !((importPreview.stats && importPreview.stats.mapped) > 0)
                            }
                        >
                            {importCommitting ? t('layouts.importCommitting') : t('layouts.importCreate')}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        venues: state.venues.venues,
        layoutsByVenue: state.venues.layoutsByVenue,
        currentLayout: state.venues.currentLayout,
        isLoading: state.venues.isLoading,
        isMutating: state.venues.isMutating,
        merchant: state.auth.merchant,
        stations: state.settings.stations
    };
}

export default connect(mapStateToProps)(withTranslation(LayoutSettings));
